import React, {useEffect, useRef, useState} from 'react';
import {
    CommandBar,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogType,
    getTheme,
    IColumn,
    ICommandBarItemProps,
    MessageBar,
    MessageBarType,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Text
} from '@fluentui/react';
import {useLazyGetExportExtensionSuggestionsQuery} from '../../store/Api';
import {useLazyGetWorkspaceRecordLinksQuery} from '../../store/endpoints/RecordLinksEndpoints';
import {IExtensionOption} from '../../models/Extension';
import {describeAge} from './OpportunityPickerDialog';
import FilterableSortableColumnHeader from '../ExtensionModal/SortableDirectImportPreviewHeader';

interface IOpportunityBrowserDialogProps {
    /** The opportunity direct-import (sub-)extension whose PSA the listing runs against. */
    extensionId: string;
    /** The company whose opportunities are listed. */
    companyId: string;
    companyName: string;
    onDismiss: () => void;
    /** Called with the chosen opportunity's id; the caller drives the quick-flow from there. */
    onPick: (opportunityId: string) => void;
}

const PAGE_SIZE = 25;
const OPPORTUNITY_SEARCH_FIELD = 'opportunitySearch';

/** The sort keys the extension's browse mode understands, keyed by column. */
const SORTABLE: Record<string, string> = {name: 'name', lastUpdated: 'lastUpdated', status: 'status'};
/** The columns the extension's browse mode can contains-filter on. */
const FILTERABLE: Record<string, boolean> = {name: true, status: true, salesRep: true};

const theme = getTheme();

const formatTotal = (value: unknown): string => {
    if (typeof value !== 'number') return '';
    try {
        return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(value);
    } catch {
        return `$${value}`;
    }
};

/**
 * The "More" picker behind the company-scoped opportunity dropdown: every opportunity of the company
 * (closed ones included, since won opportunities are legitimate import sources), 25 per page, filterable
 * by name and sortable by the columns the PSA can order on. Picking a row hands the opportunity id back
 * exactly like the dropdown does.
 */
const OpportunityBrowserDialog: React.FC<IOpportunityBrowserDialogProps> = (props) => {
    const [getSuggestions] = useLazyGetExportExtensionSuggestionsQuery();
    const [getRecordLinks] = useLazyGetWorkspaceRecordLinksQuery();
    const [rows, setRows] = useState<IExtensionOption[]>([]);
    const [totalCount, setTotalCount] = useState<number>();
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('lastUpdated');
    const [sortDescending, setSortDescending] = useState(true);
    // One column filter at a time, matching the direct-import preview's contract.
    const [filterField, setFilterField] = useState<string>();
    const [filterValue, setFilterValue] = useState<string>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [imported, setImported] = useState<Record<string, string>>({});
    const fetchSequence = useRef(0);

    useEffect(() => {
        const sequence = ++fetchSequence.current;
        setLoading(true);
        (async () => {
            try {
                const result = await getSuggestions({
                    extensionId: props.extensionId,
                    field: OPPORTUNITY_SEARCH_FIELD,
                    limit: PAGE_SIZE,
                    page,
                    sortBy,
                    sortByDescending: sortDescending,
                    filterField: filterValue ? filterField : undefined,
                    filterValue: filterValue || undefined,
                    additionalData: {company: props.companyId}
                }).unwrap();
                if (sequence !== fetchSequence.current) return;
                setError(result?.success === false ? (result.error || 'The extension failed to list opportunities') : undefined);
                const options = result?.options ?? [];
                setRows(options);
                setTotalCount(result?.totalCount);
                const links = options.length > 0
                    ? await getRecordLinks({field: 'opportunityId', externalIds: options.map((o) => o.value)})
                        .unwrap().catch(() => [])
                    : [];
                if (sequence !== fetchSequence.current) return;
                setImported(Object.fromEntries(links.map((l) => [l.externalId, l.workspaceId])));
            } catch {
                if (sequence === fetchSequence.current) setError('The extension failed to list opportunities');
            } finally {
                if (sequence === fetchSequence.current) setLoading(false);
            }
        })();
    }, [props.extensionId, props.companyId, page, sortBy, sortDescending, filterField, filterValue]);

    const onSort = (key?: string) => {
        if (!key || !SORTABLE[key]) return;
        setPage(1);
        if (sortBy === key) setSortDescending((d) => !d);
        else {
            setSortBy(key);
            setSortDescending(key === 'lastUpdated');
        }
    };

    const onFilterApply = (key: string) => (value?: string) => {
        setPage(1);
        setFilterField(key);
        setFilterValue(value || undefined);
    };

    // The direct-import preview's column header: sort toggle + funnel filter per column.
    const header = (label: string, key: string) => () => (
        <FilterableSortableColumnHeader
            label={label}
            field={key}
            sortable={!!SORTABLE[key]}
            sortedDescending={sortBy === key ? sortDescending : undefined}
            onSort={onSort}
            filterable={!!FILTERABLE[key]}
            filterValue={filterField === key ? filterValue : undefined}
            onFilterApply={onFilterApply(key)}
        />
    );

    const extra = (option: IExtensionOption, key: string) => option.additionalProperties?.[key];

    const columns: IColumn[] = [
        {
            key: 'name', name: 'Opportunity', minWidth: 180, isResizable: true, isMultiline: true,
            onRenderHeader: header('Opportunity', 'name'),
            onRender: (option: IExtensionOption) => (
                <span>
                    {option.display}
                    {imported[option.value] &&
                        <Text variant='small' style={{marginLeft: 6, color: theme.palette.neutralSecondary}}>
                            Already imported. Opens its workspace.
                        </Text>}
                </span>
            )
        },
        {
            key: 'status', name: 'Status', minWidth: 72, maxWidth: 96, isResizable: true,
            onRenderHeader: header('Status', 'status'),
            onRender: (option: IExtensionOption) => <span>{String(extra(option, 'status') ?? '')}</span>
        },
        {
            key: 'total', name: 'Total', minWidth: 80, maxWidth: 100,
            onRender: (option: IExtensionOption) => <span>{formatTotal(extra(option, 'total'))}</span>
        },
        {
            key: 'salesRep', name: 'Sales Rep', minWidth: 100, maxWidth: 140, isResizable: true,
            onRenderHeader: header('Sales Rep', 'salesRep'),
            onRender: (option: IExtensionOption) => <span>{String(extra(option, 'salesRep') ?? '')}</span>
        },
        {
            key: 'lastUpdated', name: 'Updated', minWidth: 100, maxWidth: 130,
            onRenderHeader: header('Updated', 'lastUpdated'),
            onRender: (option: IExtensionOption) =>
                <span>{describeAge(extra(option, 'lastUpdated') ? String(extra(option, 'lastUpdated')) : undefined) ?? ''}</span>
        }
    ];

    const pageCount = totalCount != null ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : undefined;

    // The grid composer's pager shape: chevrons around an "X-Y of N" button whose menu jumps pages.
    const startCount = totalCount ? (page - 1) * PAGE_SIZE + 1 : 0;
    const endCount = totalCount ? Math.min(page * PAGE_SIZE, totalCount) : 0;
    const pageCommands: ICommandBarItemProps[] = Array.from({length: Math.min(pageCount ?? 0, 10)}, (_, i) => ({
        key: `page-${i + 1}`,
        text: `Page ${i + 1}`,
        checked: page === i + 1,
        disabled: page === i + 1,
        onClick: () => setPage(i + 1)
    }));
    const pagerItems: ICommandBarItemProps[] = [
        {
            key: 'previous',
            text: 'Previous',
            iconOnly: true,
            iconProps: {iconName: 'ChevronLeft'},
            disabled: loading || page <= 1,
            onClick: () => setPage((p) => Math.max(1, p - 1))
        },
        {
            key: 'page',
            text: totalCount != null
                ? (totalCount > 0 ? `${startCount}-${endCount} of ${totalCount}` : 'No Results')
                : `Page ${page}`,
            subMenuProps: pageCommands.length > 1 ? {items: pageCommands} : undefined
        },
        {
            key: 'next',
            text: 'Next',
            iconOnly: true,
            iconProps: {iconName: 'ChevronRight'},
            disabled: loading || (pageCount != null ? page >= pageCount : rows.length < PAGE_SIZE),
            onClick: () => setPage((p) => p + 1)
        }
    ];

    return (
        <Dialog
            hidden={false}
            onDismiss={props.onDismiss}
            minWidth={760}
            dialogContentProps={{
                type: DialogType.normal,
                title: `${props.companyName} Opportunities`,
                subText: 'Every opportunity for this company. Pick one to build its workspace and prepared cart.'
            }}>
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            <div style={{minHeight: 300, maxHeight: 420, overflowY: 'auto'}}>
                {loading
                    ? <Spinner size={SpinnerSize.large} label='Loading opportunities' styles={{root: {marginTop: '3em'}}} />
                    : <DetailsList
                        items={rows}
                        columns={columns}
                        selectionMode={SelectionMode.none}
                        layoutMode={DetailsListLayoutMode.justified}
                        onItemInvoked={(option: IExtensionOption) => props.onPick(option.value)}
                        // The whole row is the pick: one click creates the cart from that opportunity.
                        onRenderRow={(rowProps, defaultRender) => rowProps && defaultRender
                            ? <div
                                role='button'
                                style={{cursor: 'pointer'}}
                                aria-label={`Create prepared cart from ${(rowProps.item as IExtensionOption).display}`}
                                onClick={() => props.onPick((rowProps.item as IExtensionOption).value)}>
                                {defaultRender(rowProps)}
                            </div>
                            : null}
                    />}
                {!loading && rows.length === 0 && !error &&
                    <Text variant='small' style={{color: theme.palette.neutralTertiary}}>
                        No opportunities match{filterValue ? ' the filter' : ''}.
                    </Text>}
            </div>
            <CommandBar
                items={[]}
                farItems={pagerItems}
                styles={{root: {marginTop: 4, paddingLeft: 0, paddingRight: 0}}}
            />
        </Dialog>
    );
};

export default OpportunityBrowserDialog;
